import { getRuntimeConfig } from '../../app/runtimeConfig';
import type {
  Alert,
  AuthConfig,
  Camera,
  Detection,
  IdentityProfile,
  IdentityVerification,
  ListResponse,
  PresignedUpload,
  Principal,
  Subject,
  SubjectFace,
  Tenant,
  TenantDirectoryUser,
  TenantSettings,
  WebhookEndpoint,
} from '../../app/types';
import { apiRequest } from './client';

export function getAuthConfig() {
  return apiRequest<AuthConfig>({ path: '/v1/auth/config', method: 'GET' });
}

export function getCurrentPrincipal(headers: Record<string, string>) {
  return apiRequest<Principal>({
    path: '/v1/auth/me',
    method: 'GET',
    headers,
  });
}

export function listTenants(headers: Record<string, string>) {
  return apiRequest<ListResponse<Tenant>>({
    path: '/v1/tenants',
    method: 'GET',
    headers,
  });
}

export function createTenant(
  headers: Record<string, string>,
  payload: { displayName: string; settings?: Partial<TenantSettings> }
) {
  return apiRequest<Tenant>({
    path: '/v1/tenants',
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
}

export function listTenantUsers(headers: Record<string, string>, tenantId: string) {
  return apiRequest<ListResponse<TenantDirectoryUser>>({
    path: `/v1/tenant-users?tenantId=${encodeURIComponent(tenantId)}`,
    method: 'GET',
    headers,
  });
}

export function createTenantUser(
  headers: Record<string, string>,
  payload: {
    tenantId: string;
    email: string;
    role: 'platform-admin' | 'tenant-admin' | 'operator' | 'viewer';
    username?: string;
    temporaryPassword?: string;
    sendInvite?: boolean;
  }
) {
  return apiRequest<TenantDirectoryUser>({
    path: '/v1/tenant-users',
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
}

export function updateTenantUser(
  headers: Record<string, string>,
  username: string,
  payload: {
    tenantId: string;
    role?: 'platform-admin' | 'tenant-admin' | 'operator' | 'viewer';
    enabled?: boolean;
  }
) {
  return apiRequest<TenantDirectoryUser>({
    path: `/v1/tenant-users/${encodeURIComponent(username)}`,
    method: 'PATCH',
    headers,
    body: JSON.stringify(payload),
  });
}

export function resetTenantUserPassword(headers: Record<string, string>, username: string, tenantId: string) {
  return apiRequest<{ accepted: boolean; username: string; tenantId: string }>({
    path: `/v1/tenant-users/${encodeURIComponent(username)}/reset-password`,
    method: 'POST',
    headers,
    body: JSON.stringify({ tenantId }),
  });
}

export function resendTenantUserInvite(headers: Record<string, string>, username: string, tenantId: string) {
  return apiRequest<{ accepted: boolean; username: string; tenantId: string }>({
    path: `/v1/tenant-users/${encodeURIComponent(username)}/resend-invite`,
    method: 'POST',
    headers,
    body: JSON.stringify({ tenantId }),
  });
}

export function getTenant(headers: Record<string, string>, tenantId: string) {
  return apiRequest<Tenant>({
    path: `/v1/tenants/${tenantId}`,
    method: 'GET',
    headers,
  });
}

export function updateTenantSettings(
  headers: Record<string, string>,
  tenantId: string,
  payload: Partial<TenantSettings>
) {
  return apiRequest<Tenant>({
    path: `/v1/tenants/${tenantId}/settings`,
    method: 'PATCH',
    headers,
    body: JSON.stringify(payload),
  });
}

export function listCameras(headers: Record<string, string>, tenantId: string) {
  return apiRequest<ListResponse<Camera>>({
    path: `/v1/cameras?tenantId=${encodeURIComponent(tenantId)}`,
    method: 'GET',
    headers,
  });
}

export function createCamera(headers: Record<string, string>, payload: { tenantId: string; name: string; status?: string }) {
  return apiRequest<Camera>({
    path: '/v1/cameras',
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
}

export function createUploadUrl(
  headers: Record<string, string>,
  payload: { tenantId: string; cameraId: string; contentType?: string }
) {
  return apiRequest<PresignedUpload>({
    path: '/v1/uploads/presign',
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
}

export async function uploadSnapshot(presignedUpload: PresignedUpload, file: File) {
  const response = await fetch(presignedUpload.uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type || 'image/jpeg',
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`Snapshot upload failed with status ${response.status}.`);
  }
}

export function listSubjects(headers: Record<string, string>, tenantId: string) {
  return apiRequest<ListResponse<Subject>>({
    path: `/v1/watchlist/subjects?tenantId=${encodeURIComponent(tenantId)}`,
    method: 'GET',
    headers,
  });
}

export function createSubject(
  headers: Record<string, string>,
  payload: { tenantId: string; displayName: string; notes?: string }
) {
  return apiRequest<Subject>({
    path: '/v1/watchlist/subjects',
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
}

export function addSubjectFace(
  headers: Record<string, string>,
  subjectId: string,
  payload: { tenantId: string; sourceImageName: string; imageBase64: string }
) {
  return apiRequest<SubjectFace>({
    path: `/v1/watchlist/subjects/${subjectId}/faces`,
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
}

export function listIdentityProfiles(headers: Record<string, string>, tenantId: string) {
  return listFilteredIdentityProfiles(headers, tenantId, {});
}

export function listFilteredIdentityProfiles(
  headers: Record<string, string>,
  tenantId: string,
  filters: { status?: string; search?: string }
) {
  const searchParams = new URLSearchParams({ tenantId });
  if (filters.status) {
    searchParams.set('status', filters.status);
  }
  if (filters.search) {
    searchParams.set('search', filters.search);
  }
  return apiRequest<ListResponse<IdentityProfile>>({
    path: `/v1/identity-profiles?${searchParams.toString()}`,
    method: 'GET',
    headers,
  });
}

export function createIdentityProfile(
  headers: Record<string, string>,
  payload: { tenantId: string; idNumber: string; name: string; surname: string; phoneNumber: string }
) {
  return apiRequest<IdentityProfile>({
    path: '/v1/identity-profiles',
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
}

export function getIdentityProfile(headers: Record<string, string>, tenantId: string, profileId: string) {
  return apiRequest<IdentityProfile>({
    path: `/v1/identity-profiles/${profileId}?tenantId=${encodeURIComponent(tenantId)}`,
    method: 'GET',
    headers,
  });
}

export function updateIdentityProfile(
  headers: Record<string, string>,
  profileId: string,
  payload: { tenantId: string; idNumber?: string; name?: string; surname?: string; phoneNumber?: string }
) {
  return apiRequest<IdentityProfile>({
    path: `/v1/identity-profiles/${profileId}`,
    method: 'PATCH',
    headers,
    body: JSON.stringify(payload),
  });
}

export function deleteIdentityProfile(headers: Record<string, string>, profileId: string, tenantId: string) {
  return apiRequest<void>({
    path: `/v1/identity-profiles/${profileId}?tenantId=${encodeURIComponent(tenantId)}`,
    method: 'DELETE',
    headers,
  });
}

export function uploadIdentityReferencePhoto(
  headers: Record<string, string>,
  profileId: string,
  payload: { tenantId: string; sourceImageName: string; imageBase64: string }
) {
  return apiRequest<IdentityProfile>({
    path: `/v1/identity-profiles/${profileId}/reference-photo`,
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
}

export function createIdentityVerification(
  headers: Record<string, string>,
  payload: { tenantId: string; idNumber: string; sourceImageName: string; imageBase64: string }
) {
  return apiRequest<IdentityVerification>({
    path: '/v1/identity-verifications',
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
}

export function listIdentityVerifications(headers: Record<string, string>, tenantId: string) {
  return listFilteredIdentityVerifications(headers, tenantId, {});
}

export function listFilteredIdentityVerifications(
  headers: Record<string, string>,
  tenantId: string,
  filters: { status?: string; reviewStatus?: string; idNumber?: string }
) {
  const searchParams = new URLSearchParams({ tenantId });
  if (filters.status) {
    searchParams.set('status', filters.status);
  }
  if (filters.reviewStatus) {
    searchParams.set('reviewStatus', filters.reviewStatus);
  }
  if (filters.idNumber) {
    searchParams.set('idNumber', filters.idNumber);
  }
  return apiRequest<ListResponse<IdentityVerification>>({
    path: `/v1/identity-verifications?${searchParams.toString()}`,
    method: 'GET',
    headers,
  });
}

export function reviewIdentityVerification(
  headers: Record<string, string>,
  verificationId: string,
  payload: { tenantId: string; reviewStatus: 'approved' | 'rejected'; reviewNotes?: string }
) {
  return apiRequest<IdentityVerification>({
    path: `/v1/identity-verifications/${verificationId}/review`,
    method: 'PATCH',
    headers,
    body: JSON.stringify(payload),
  });
}

export function listAlerts(headers: Record<string, string>, tenantId: string) {
  return apiRequest<ListResponse<Alert>>({
    path: `/v1/alerts?tenantId=${encodeURIComponent(tenantId)}`,
    method: 'GET',
    headers,
  });
}

export function listDetections(headers: Record<string, string>, tenantId: string) {
  return apiRequest<ListResponse<Detection>>({
    path: `/v1/detections?tenantId=${encodeURIComponent(tenantId)}`,
    method: 'GET',
    headers,
  });
}

export function listWebhooks(headers: Record<string, string>, tenantId: string) {
  return apiRequest<ListResponse<WebhookEndpoint>>({
    path: `/v1/webhooks?tenantId=${encodeURIComponent(tenantId)}`,
    method: 'GET',
    headers,
  });
}

export function createWebhook(
  headers: Record<string, string>,
  payload: { tenantId: string; url: string; enabled?: boolean; signingSecret?: string }
) {
  return apiRequest<WebhookEndpoint>({
    path: '/v1/webhooks',
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
}

export function testWebhook(headers: Record<string, string>, webhookId: string, tenantId: string) {
  return apiRequest<{ accepted: boolean; webhookId: string; tenantId: string; queued: boolean }>({
    path: `/v1/webhooks/${webhookId}/test?tenantId=${encodeURIComponent(tenantId)}`,
    method: 'POST',
    headers,
  });
}

export function getFrontendEnvironmentSummary() {
  const runtime = getRuntimeConfig();
  return {
    stage: runtime.stage,
    apiBaseUrl: runtime.apiBaseUrl,
    region: runtime.region,
  };
}
